import { useEffect, useMemo, useState } from 'react'
import { Smartphone, X } from '@/ui/icons'

const DISMISS_KEY = 'sitrepp.appHandoff.dismissedAt'
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

const APP_STORE_URL = 'https://apps.apple.com/app/sitrepp/id0000000000'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.sitrepp.mobile'

type Platform = 'ios' | 'android' | 'other'

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other'
  const ua = navigator.userAgent || ''
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'other'
}

function isRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const ts = Number(raw)
    if (!Number.isFinite(ts)) return false
    return Date.now() - ts < DISMISS_WINDOW_MS
  } catch {
    return false
  }
}

interface AppHandoffBannerProps {
  token: string
}

export function AppHandoffBanner({ token }: AppHandoffBannerProps) {
  const platform = useMemo(detectPlatform, [])
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (platform === 'other') return
    if (isRecentlyDismissed()) return
    setVisible(true)
  }, [platform])

  if (!visible || platform === 'other') return null

  const storeUrl = platform === 'ios' ? APP_STORE_URL : PLAY_STORE_URL
  const storeLabel = platform === 'ios' ? 'App Store' : 'Google Play'

  const handleOpenInApp = () => {
    // Universal/App Links: navigating to the https URL is intercepted by the OS
    // when the app is installed and the domain is verified. If not installed,
    // the browser stays here and the store fallback below remains visible.
    const httpsUrl = `${window.location.origin}/share/${token}`
    const schemeUrl = `sitrepp://share?token=${encodeURIComponent(token)}`

    // Try the custom scheme as a belt-and-suspenders fallback on Android,
    // where some browsers (in-app webviews) don't honor App Links.
    if (platform === 'android') {
      window.location.href = schemeUrl
      window.setTimeout(() => {
        window.location.href = httpsUrl
      }, 400)
      return
    }
    window.location.href = httpsUrl
  }

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()))
    } catch {
      // ignore
    }
    setVisible(false)
  }

  return (
    <div className="sticky top-0 z-40 w-full border-b border-earth-200 bg-white shadow-sm">
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-earth-100">
          <Smartphone className="h-5 w-5 text-earth-700" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-earth-900">Open in the SitRepp app</p>
          <p className="truncate text-xs text-muted-foreground">
            Get the full trip experience on your phone.
          </p>
        </div>
        <button
          type="button"
          onClick={handleOpenInApp}
          className="shrink-0 rounded-full bg-earth-900 px-4 py-2 text-xs font-semibold text-white hover:bg-earth-800"
        >
          Open
        </button>
        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden shrink-0 rounded-full border border-earth-300 px-3 py-2 text-xs font-semibold text-earth-800 hover:bg-earth-50 sm:inline-flex"
        >
          {storeLabel}
        </a>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded-full p-1.5 text-earth-500 hover:bg-earth-100 hover:text-earth-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

export default AppHandoffBanner
